import { z } from 'zod';
import { IsoDateTime, NonEmptyName, Uuid } from '../primitives.js';

/**
 * Webhooks + Zapier contract (Phase 4). NOTE: ApiScope + SCOPES are owned by the SPINE in
 * entities/auth.ts (CONTRACT amendment B) — this file does NOT declare them, it only consumes them.
 * WebhookEventType mirrors DomainEventType from src/events/bus.ts verbatim (kept in lockstep by a
 * unit test). Entity/response schemas carry `.meta({ id })`; query schemas must NOT.
 */

// ── Enums ────────────────────────────────────────────────────────────────────
/** The public, documented event vocabulary subscriptions filter on (mirrors DomainEventType). */
export const WebhookEventType = z.enum([
  'appointment.created',
  'appointment.updated',
  'appointment.cancelled',
  // WS13 — emitted exactly once when an appointment transitions to 'completed' (the reviews trigger).
  'appointment.completed',
  'payment.succeeded',
  'payment.refunded',
  'receipt.issued',
  'queue.ticket.joined',
  'queue.ticket.called',
  'comms.message.sent',
  'stock.low',
]);
export type WebhookEventType = z.infer<typeof WebhookEventType>;

export const WebhookDeliveryStatus = z.enum([
  'pending',
  'delivering',
  'succeeded',
  'failed',
  'dead',
]);
export type WebhookDeliveryStatus = z.infer<typeof WebhookDeliveryStatus>;

// ── Endpoints ──────────────────────────────────────────────────────────────────
/** Response schema — NEVER includes `secret`. The secret is returned once via WebhookEndpointCreated. */
export const WebhookEndpoint = z
  .object({
    id: Uuid,
    salonId: Uuid,
    url: z.url(),
    eventTypes: z.array(WebhookEventType),
    description: z.string().nullable(),
    source: z.enum(['api', 'zapier']),
    active: z.boolean(),
    lastDeliveryAt: IsoDateTime.nullable(),
    consecutiveFailures: z.int().min(0),
    disabledAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'WebhookEndpoint' });
export type WebhookEndpoint = z.infer<typeof WebhookEndpoint>;

/** Returned only from POST /v1/webhooks (and rotateSecret) — the signing secret shown once. */
export const WebhookEndpointCreated = WebhookEndpoint.extend({
  secret: z.string(),
}).meta({ id: 'WebhookEndpointCreated' });
export type WebhookEndpointCreated = z.infer<typeof WebhookEndpointCreated>;

export const CreateWebhookEndpoint = z
  .object({
    url: z.url().max(2048),
    eventTypes: z.array(WebhookEventType).min(1),
    description: NonEmptyName.optional(),
    secret: z.string().min(16).max(200).optional(),
  })
  .meta({ id: 'CreateWebhookEndpoint' });
export type CreateWebhookEndpoint = z.infer<typeof CreateWebhookEndpoint>;

export const UpdateWebhookEndpoint = z
  .object({
    url: z.url().max(2048).optional(),
    eventTypes: z.array(WebhookEventType).min(1).optional(),
    description: NonEmptyName.nullish(),
    active: z.boolean().optional(),
    rotateSecret: z.boolean().optional(),
  })
  .meta({ id: 'UpdateWebhookEndpoint' });
export type UpdateWebhookEndpoint = z.infer<typeof UpdateWebhookEndpoint>;

// ── Deliveries + envelope ────────────────────────────────────────────────────────
export const WebhookDelivery = z
  .object({
    id: Uuid,
    salonId: Uuid,
    endpointId: Uuid,
    eventId: Uuid,
    eventType: WebhookEventType,
    status: WebhookDeliveryStatus,
    attempts: z.int().min(0),
    maxAttempts: z.int().min(1),
    responseCode: z.int().nullable(),
    error: z.string().nullable(),
    payload: z.record(z.string(), z.unknown()),
    nextAttemptAt: IsoDateTime.nullable(),
    deliveredAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'WebhookDelivery' });
export type WebhookDelivery = z.infer<typeof WebhookDelivery>;

/** The exact JSON body POSTed to subscribers (also the Zapier sample payload). */
export const WebhookEventEnvelope = z
  .object({
    id: Uuid,
    type: WebhookEventType,
    salonId: Uuid,
    createdAt: IsoDateTime,
    data: z.record(z.string(), z.unknown()),
  })
  .meta({ id: 'WebhookEventEnvelope' });
export type WebhookEventEnvelope = z.infer<typeof WebhookEventEnvelope>;

// ── Zapier DTOs ────────────────────────────────────────────────────────────────
export const ZapierSubscribeRequest = z
  .object({
    targetUrl: z.url().max(2048),
    event: WebhookEventType,
  })
  .meta({ id: 'ZapierSubscribeRequest' });
export type ZapierSubscribeRequest = z.infer<typeof ZapierSubscribeRequest>;

export const ZapierSubscription = z
  .object({
    id: Uuid,
    targetUrl: z.url(),
    event: WebhookEventType,
  })
  .meta({ id: 'ZapierSubscription' });
export type ZapierSubscription = z.infer<typeof ZapierSubscription>;

export const ZapierAuthTest = z
  .object({
    salonId: Uuid,
    salonName: z.string(),
    staffId: Uuid.nullable(),
    scopes: z.array(z.string()),
  })
  .meta({ id: 'ZapierAuthTest' });
export type ZapierAuthTest = z.infer<typeof ZapierAuthTest>;
